import { useState } from 'react';
import { useSession } from '../../app/SessionProvider';
import { useI18n } from '../../i18n/I18nProvider';
import { Avatar, LangSwitch } from '../../ui/controls';
import { ChartColumn, MessageSquare } from '../../ui/icons';
import { Alert } from '../../ui/feedback';
import { HomeButton, Screen, ScrollArea, SectionTitle } from '../../ui/Screen';
import { LinkRow } from './StartScreen';
import { RestrictionSheet } from './RestrictionSheet';

/**
 * Профиль КЛИЕНТА. Только клиентская ветка: входа в кабинет бизнеса здесь нет
 * (21.09.2026) — бизнес входит со стартового экрана («Принимать записи»), и профиль
 * клиента не должен уводить в чужую ветку. У бизнеса свой «профиль» — вкладка «Настройки».
 */
export function ProfileScreen(props: { onFeedback: () => void; onAdmin: () => void }): JSX.Element {
  const { t } = useI18n();
  const session = useSession();
  const user = session.state.status === 'ready' ? session.state.user : null;
  const name = user?.firstName ?? null;
  const [rulesOpen, setRulesOpen] = useState(false);

  return (
    <Screen>
      <ScrollArea bottomGap="tabs">
        <div className="flex items-center gap-3 px-4 pb-1 pt-[calc(env(safe-area-inset-top,0px)+18px)]">
          {name ? <Avatar name={name} /> : null}
          <div className="min-w-0 flex-1 truncate text-[24px] font-extrabold tracking-[-0.03em]">
            {name ?? t('profileTitle')}
          </div>
          <HomeButton />
        </div>

        <div className="px-4 pt-6">
          <SectionTitle>{t('profileLang')}</SectionTitle>
          <div className="w-fit">
            <LangSwitch />
          </div>
        </div>

        {/* Без сессии счётчика нет — блок не рисуется, а не показывает «0»: ноль
            читался бы как «неявок нет», хотя мы этого просто не знаем. */}
        {user ? (
          <div className="px-4 pt-6">
            <SectionTitle>{t('profNoShowT')}</SectionTitle>
            <Alert
              tone={user.noShowCount === 0 ? 'plain' : user.noShowCount === 1 ? 'warn' : 'danger'}
              title={String(user.noShowCount)}
              action={{ label: t('profRules'), onClick: () => setRulesOpen(true) }}
            >
              {user.noShowCount === 0 ? t('profNoShowNone') : t('profNoShowSome', { n: user.noShowCount })}
            </Alert>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 px-4 pt-6">
          <LinkRow icon={MessageSquare} label={t('fbOpen')} onClick={props.onFeedback} />
          {/* ponytail: подпись строкой, а не ключом i18n. Экран админки видит один человек —
              владелец продукта (`isAdmin`), он русскоязычный, узбекской версии у внутренней
              статистики нет. Заводить ключи RU+UZ ради одной невидимой всем строки незачем. */}
          {user?.isAdmin ? (
            <LinkRow icon={ChartColumn} label="Статистика платформы" onClick={props.onAdmin} />
          ) : null}
        </div>
      </ScrollArea>
      <RestrictionSheet open={rulesOpen} restriction={null} onClose={() => setRulesOpen(false)} />
    </Screen>
  );
}
